import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { getDashboard } from "src/apis/controllers/dashboard";
import DashboardSummary from "./DashboardSummary";
import DashboardTypeList from "./DashboardTypeList";
import { normalizeDashboardData } from "./dashboardData";

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;

  return (
    error?.message ||
    error?.response?.message ||
    "Δεν ήταν δυνατή η φόρτωση του dashboard."
  );
};

const DashboardLoading = () => (
  <Box
    role="status"
    sx={{ minHeight: 360, display: "grid", placeItems: "center" }}
  >
    <Stack alignItems="center" spacing={1.5}>
      <CircularProgress />
      <Typography color="text.secondary">Φόρτωση στοιχείων…</Typography>
    </Stack>
  </Box>
);

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [activeCategoryKey, setActiveCategoryKey] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");

    getDashboard()
      .then((response) => {
        if (cancelled) return;

        const normalized = normalizeDashboardData(response?.data);
        setDashboard(normalized);
        setActiveCategoryKey((currentKey) =>
          normalized.categories.some(
            (category) => category.key === currentKey
          )
            ? currentKey
            : normalized.categories[0]?.key ?? ""
        );
      })
      .catch((requestError) => {
        if (cancelled) return;
        setDashboard(null);
        setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestVersion]);

  const activeCategory =
    dashboard?.categories.find(
      (category) => category.key === activeCategoryKey
    ) ?? dashboard?.categories[0];

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        maxWidth: 1500,
        boxSizing: "border-box",
        mx: "auto",
        px: { xs: 1.5, sm: 2.5, lg: 3 },
        py: 2.5,
      }}
    >
      {loading ? (
        <DashboardLoading />
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setRequestVersion((version) => version + 1)}
            >
              Νέα προσπάθεια
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
          >
            {dashboard.categories.length > 0 && (
              <Tabs
                value={activeCategory?.key ?? false}
                onChange={(_, value) => setActiveCategoryKey(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Κατηγορίες dashboard"
                sx={{
                  minWidth: 0,
                  minHeight: 40,
                  flex: "1 1 auto",
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTab-root": {
                    minHeight: 40,
                    minWidth: "auto",
                    mr: 1,
                    px: 2,
                    py: 0.75,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 10,
                    bgcolor: "background.paper",
                    color: "primary.main",
                    fontWeight: 700,
                    textTransform: "none",
                  },
                  "& .Mui-selected": {
                    bgcolor: "primary.main",
                    color: "#fff !important",
                    borderColor: "primary.main",
                  },
                }}
              >
                {dashboard.categories.map((category) => (
                  <Tab
                    key={category.key}
                    value={category.key}
                    label={category.title}
                  />
                ))}
              </Tabs>
            )}
            <Stack
              direction="row"
              justifyContent={{ xs: "space-between", md: "flex-end" }}
              alignItems="center"
              spacing={1.5}
              sx={{ flex: "0 0 auto" }}
            >
              <Typography variant="body2" color="text.secondary">
                {dashboard.lastUpdatedAtFormatted
                  ? `Τελευταία ενημέρωση: ${dashboard.lastUpdatedAtFormatted}`
                  : "Δεν υπάρχει χρόνος τελευταίας ενημέρωσης"}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={() =>
                  setRequestVersion((version) => version + 1)
                }
              >
                Ανανέωση
              </Button>
            </Stack>
          </Stack>

          {dashboard.categories.length === 0 ? (
            <Alert severity="info">
              Δεν υπάρχουν διαθέσιμα στοιχεία κατηγοριών.
            </Alert>
          ) : (
            activeCategory && (
              <Box
                key={activeCategory.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    lg: "minmax(0, 1.15fr) minmax(340px, 0.85fr)",
                  },
                  gap: 2,
                  alignItems: "start",
                }}
              >
                <DashboardTypeList category={activeCategory} />
                <DashboardSummary category={activeCategory} />
              </Box>
            )
          )}

        </Stack>
      )}
    </Box>
  );
};

export default Dashboard;
